// Knowledge base — fetches documented trading rules + viral quotes per legend.
// Reads from the public `legend_knowledge` table (RLS allows anon SELECT).
import { supabase } from "@/integrations/supabase/client";

export interface LegendRule {
  id: string;
  legendName: string;
  agentName: string;
  category: "entry" | "exit" | "risk" | "psychology" | "viral_quote";
  ruleText: string;
  sourceBook: string;
  viralScore: number;
}

export interface RagContext {
  signal: "BUY" | "SELL" | "WAIT";
  vpin: number;
  sentiment: number;
  nearNews: boolean;
  hasPattern: boolean;
}

interface CacheEntry<T> {
  value: T;
  at: number;
}

const RULES_TTL_MS = 5 * 60 * 1000;
const QUOTE_TTL_MS = 60 * 60 * 1000;

export class KnowledgeBase {
  private rulesCache = new Map<string, CacheEntry<LegendRule[]>>();
  private quoteCache = new Map<string, CacheEntry<string>>();

  async getRelevantRules(
    legendName: string,
    context: RagContext,
  ): Promise<LegendRule[]> {
    const key = `${legendName}|${context.signal}|${context.nearNews ? 1 : 0}|${
      context.hasPattern ? 1 : 0
    }`;
    const cached = this.rulesCache.get(key);
    if (cached && Date.now() - cached.at < RULES_TTL_MS) return cached.value;

    const { data, error } = await supabase
      .from("legend_knowledge")
      .select("id, legend_name, agent_name, category, rule_text, source_book, viral_score")
      .eq("legend_name", legendName)
      .neq("category", "viral_quote")
      .limit(20);

    if (error || !data || data.length === 0) {
      this.rulesCache.set(key, { value: [], at: Date.now() });
      return [];
    }

    const scored = data
      .map((r) => ({ row: r, score: this.score(r, context) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map<LegendRule>(({ row }) => ({
        id: row.id,
        legendName: row.legend_name,
        agentName: row.agent_name,
        category: row.category as LegendRule["category"],
        ruleText: row.rule_text,
        sourceBook: row.source_book ?? "",
        viralScore: row.viral_score ?? 0,
      }));

    this.rulesCache.set(key, { value: scored, at: Date.now() });
    return scored;
  }

  async getViralQuote(legendName: string): Promise<LegendRule | null> {
    const cached = this.quoteCache.get(legendName);
    if (cached && Date.now() - cached.at < QUOTE_TTL_MS) {
      // We stored only ruleText in quoteCache; refetch full record sparingly.
    }
    const { data } = await supabase
      .from("legend_knowledge")
      .select("id, legend_name, agent_name, category, rule_text, source_book, viral_score")
      .eq("legend_name", legendName)
      .eq("category", "viral_quote")
      .order("viral_score", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!data) return null;
    return {
      id: data.id,
      legendName: data.legend_name,
      agentName: data.agent_name,
      category: "viral_quote",
      ruleText: data.rule_text,
      sourceBook: data.source_book ?? "",
      viralScore: data.viral_score ?? 0,
    };
  }

  async getTopViralQuotes(): Promise<Record<string, LegendRule>> {
    const { data } = await supabase
      .from("legend_knowledge")
      .select("id, legend_name, agent_name, category, rule_text, source_book, viral_score")
      .eq("category", "viral_quote")
      .order("viral_score", { ascending: false });
    const map: Record<string, LegendRule> = {};
    (data ?? []).forEach((r) => {
      if (!map[r.legend_name]) {
        map[r.legend_name] = {
          id: r.id,
          legendName: r.legend_name,
          agentName: r.agent_name,
          category: "viral_quote",
          ruleText: r.rule_text,
          sourceBook: r.source_book ?? "",
          viralScore: r.viral_score ?? 0,
        };
      }
    });
    return map;
  }

  formatForPrompt(rules: LegendRule[]): string {
    if (rules.length === 0) return "";
    return (
      `\n\n📚 YOUR DOCUMENTED TRADING RULES (from verified sources):\n` +
      rules
        .map(
          (r, i) =>
            `Rule ${i + 1} [${r.category.toUpperCase()}]: ${r.ruleText}\n   Source: ${r.sourceBook}`,
        )
        .join("\n\n") +
      `\n\nApply these EXACT rules to your analysis. Cite them in your reasoning.`
    );
  }

  private score(rule: { category: string; rule_text: string; viral_score: number | null }, context: RagContext): number {
    let score = rule.viral_score ?? 0;
    const text = (rule.rule_text || "").toLowerCase();
    const cat = rule.category;

    if (context.signal !== "WAIT" && cat === "entry") score += 30;
    if (cat === "risk") score += 20;
    if (context.vpin > 0.7 && text.includes("breakout")) score += 30;
    if (context.nearNews && cat === "psychology") score += 20;
    if (Math.abs(context.sentiment) > 65 && text.includes("sentiment")) score += 30;
    if (context.hasPattern && text.includes("pattern")) score += 20;
    if (context.signal === "WAIT" && cat === "psychology") score += 20;
    return score;
  }
}

export const knowledgeBase = new KnowledgeBase();
